import React, { useState } from 'react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';

const COLORS = [
  '#3366FF', '#22c55e', '#f59e0b', '#dc3545', '#7c3aed',
  '#06b6d4', '#ea580c', '#8492a6', '#2952CC', '#d97706',
  '#0ea5e9', '#a855f7', '#14b8a6', '#f43f5e', '#6366f1',
];

const VIEW_GRAPH = 'graph';
const VIEW_TABLE = 'table';

/**
 * ChartCard - Unified chart component with graph/table toggle.
 *
 * Props:
 *   title       - chart title string
 *   data        - array of { name: string, count: number }
 *   chartType   - 'donut' | 'bar' (auto-detected if omitted: ≤6 items = donut, >6 = bar)
 *   height      - card height in px (default 300)
 */
export default function ChartCard({ title, data = [], chartType, height = 300 }) {
  const [view, setView] = useState(VIEW_GRAPH);

  const total = data.reduce((sum, d) => sum + (d.count || 0), 0);
  const sorted = [...data].sort((a, b) => b.count - a.count);
  const resolvedType = chartType || (sorted.length <= 6 ? 'donut' : 'bar');

  // For bar chart, show top 15 + aggregate rest
  let barData = sorted;
  if (resolvedType === 'bar' && sorted.length > 15) {
    const top = sorted.slice(0, 15);
    const restCount = sorted.slice(15).reduce((s, d) => s + d.count, 0);
    barData = [...top, { name: `Others (${sorted.length - 15})`, count: restCount }];
  }

  return (
    <div style={{
      backgroundColor: '#fff',
      borderRadius: 10,
      border: '1px solid #e8ecf1',
      padding: '16px',
      height,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 8, flexShrink: 0,
      }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#1a202c' }}>{title}</span>
        <div style={{
          display: 'flex', borderRadius: 6, overflow: 'hidden',
          border: '1px solid #e8ecf1',
        }}>
          <button
            onClick={() => setView(VIEW_GRAPH)}
            style={{
              padding: '4px 10px', fontSize: 12, border: 'none', cursor: 'pointer',
              backgroundColor: view === VIEW_GRAPH ? '#3366FF' : '#fff',
              color: view === VIEW_GRAPH ? '#fff' : '#8492a6',
              fontWeight: 500,
            }}
          >
            Chart
          </button>
          <button
            onClick={() => setView(VIEW_TABLE)}
            style={{
              padding: '4px 10px', fontSize: 12, border: 'none', cursor: 'pointer',
              borderLeft: '1px solid #e8ecf1',
              backgroundColor: view === VIEW_TABLE ? '#3366FF' : '#fff',
              color: view === VIEW_TABLE ? '#fff' : '#8492a6',
              fontWeight: 500,
            }}
          >
            Table
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {data.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#8492a6', fontSize: 13 }}>
            No data available
          </div>
        ) : view === VIEW_GRAPH ? (
          resolvedType === 'donut' ? (
            <DonutView data={sorted} total={total} />
          ) : (
            <BarView data={barData} />
          )
        ) : (
          <TableView data={sorted} total={total} />
        )}
      </div>
    </div>
  );
}

/* ── Donut Chart ─────────────────────────────────────── */
function DonutView({ data, total }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={data}
          dataKey="count"
          nameKey="name"
          cx="50%"
          cy="50%"
          innerRadius="55%"
          outerRadius="80%"
          paddingAngle={2}
          stroke="none"
        >
          {data.map((_, i) => (
            <Cell key={i} fill={COLORS[i % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip
          formatter={(value, name) => [`${value} (${total ? ((value / total) * 100).toFixed(1) : 0}%)`, name]}
          contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #e8ecf1' }}
        />
        {/* Center label */}
        <text x="50%" y="46%" textAnchor="middle" fontSize={20} fontWeight={700} fill="#1a202c">
          {total.toLocaleString()}
        </text>
        <text x="50%" y="56%" textAnchor="middle" fontSize={11} fill="#8492a6">
          Total
        </text>
      </PieChart>
    </ResponsiveContainer>
  );
}

/* ── Horizontal Bar Chart ────────────────────────────── */
function BarView({ data }) {
  const chartHeight = Math.max(data.length * 28, 120);
  return (
    <div style={{ width: '100%', height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart data={data} layout="vertical" margin={{ top: 0, right: 30, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
          <XAxis type="number" tick={{ fontSize: 11, fill: '#8492a6' }} axisLine={false} tickLine={false} />
          <YAxis
            type="category"
            dataKey="name"
            width={120}
            tick={{ fontSize: 11, fill: '#3c4858' }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #e8ecf1' }}
          />
          <Bar dataKey="count" fill="#3366FF" radius={[0, 4, 4, 0]} barSize={18} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ── Table View ──────────────────────────────────────── */
function TableView({ data, total }) {
  const maxCount = data.length > 0 ? data[0].count : 1;
  return (
    <div style={{ width: '100%', height: '100%', overflowY: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #e8ecf1', position: 'sticky', top: 0, backgroundColor: '#fff' }}>
            <th style={{ textAlign: 'left', padding: '6px 8px', color: '#8492a6', fontWeight: 500, fontSize: 11 }}>Name</th>
            <th style={{ textAlign: 'right', padding: '6px 8px', color: '#8492a6', fontWeight: 500, fontSize: 11, width: 60 }}>Count</th>
            <th style={{ textAlign: 'right', padding: '6px 8px', color: '#8492a6', fontWeight: 500, fontSize: 11, width: 50 }}>%</th>
            <th style={{ padding: '6px 8px', width: '35%' }}></th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, i) => {
            const pct = total ? ((item.count / total) * 100).toFixed(1) : 0;
            const barWidth = maxCount ? (item.count / maxCount) * 100 : 0;
            return (
              <tr key={i} style={{ borderBottom: '1px solid #f5f7fa' }}>
                <td style={{ padding: '5px 8px', color: '#3c4858', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }} title={item.name}>
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, backgroundColor: COLORS[i % COLORS.length], marginRight: 6, flexShrink: 0 }} />
                  {item.name || 'No Value'}
                </td>
                <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 600, color: '#1a202c' }}>{item.count}</td>
                <td style={{ padding: '5px 8px', textAlign: 'right', color: '#8492a6' }}>{pct}%</td>
                <td style={{ padding: '5px 8px' }}>
                  <div style={{
                    height: 6, borderRadius: 3, backgroundColor: '#f0f2f5',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      height: '100%', borderRadius: 3,
                      backgroundColor: COLORS[i % COLORS.length],
                      width: `${barWidth}%`,
                      transition: 'width 0.3s ease',
                    }} />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
